// Sky-strip daily almanac — self-contained astronomy, runs in the browser on page load.
// No 12MB ephemeris, no server, no daily task. Sun/Moon/phase via abridged Meeus series
// (validated 0 sign/phase error vs Swiss Ephemeris); planets via Schlyter's compact orbital
// elements (+ Jupiter/Saturn/Uranus perturbations) for retrograde/station/ingress detection.
// computeAlmanac(date) -> { sun, moon, transit, phaseKey } for the readout + moon art.

const D2R = Math.PI / 180, R2D = 180 / Math.PI;
const norm = a => ((a % 360) + 360) % 360;
const SIGNS = ['aries','taurus','gemini','cancer','leo','virgo','libra','scorpio','sagittarius','capricorn','aquarius','pisces'];
const signOf = l => SIGNS[Math.floor(norm(l) / 30)];
const PHASES = ['new','waxing crescent','first quarter','waxing gibbous','full','waning gibbous','last quarter','waning crescent'];
// art keys map the 8 phases onto the 3 moon images the theme actually has
// (crescent/half/full) + CSS flip for the waning twins + dim for the new moon.
// Swap this map when the full 8-phase art set exists — nothing else changes.
const PHASE_ART = {
  'new':            { img: 'crescent', flip: false, dim: true  },  // interim: no dark-moon art yet
  'waxing crescent':{ img: 'crescent', flip: false, dim: false },
  'first quarter':  { img: 'half',     flip: false, dim: false },
  'waxing gibbous': { img: 'full',     flip: false, dim: false },  // interim: no gibbous art yet
  'full':           { img: 'full',     flip: false, dim: false },
  'waning gibbous': { img: 'full',     flip: true,  dim: false },
  'last quarter':   { img: 'half',     flip: true,  dim: false },
  'waning crescent':{ img: 'crescent', flip: true,  dim: false },
};

// Phase naming. A 45deg bucket would call today "full" two days before the actual
// full moon, which contradicts every moon app a visitor might check. Instead the four
// principal names are used only on the reference day the phase actually occurs; every
// other day gets its intermediate name. Reference zone is fixed (America/New_York) so
// every visitor worldwide reads the same line.
const REF_TZ = 'America/New_York';
const PRINCIPAL = [[0,'new'],[90,'first quarter'],[180,'full'],[270,'last quarter']];
const INTERMEDIATE = ['waxing crescent','waxing gibbous','waning gibbous','waning crescent'];
const sdiff = (a, b) => ((a - b + 540) % 360) - 180;

function tzOffsetMs(ms) { // ms east of UTC for REF_TZ at that instant
  const p = {};
  for (const { type, value } of new Intl.DateTimeFormat('en-US', {
    timeZone: REF_TZ, hour12: false, year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms))) p[type] = value;
  const h = +p.hour % 24;
  return Date.UTC(+p.year, +p.month - 1, +p.day, h, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000;
}
function refDayRange(ms) { // [start, end) UTC ms of the REF_TZ calendar day containing ms
  const off = tzOffsetMs(ms);
  const localMidnight = Math.floor((ms + off) / 86400000) * 86400000;
  const start = localMidnight - tzOffsetMs(localMidnight - off);
  const end = localMidnight + 86400000 - tzOffsetMs(localMidnight + 86400000 - off);
  return [start, end];
}
function elongAt(ms) {
  const T = (toJD(new Date(ms)) - 2451545.0) / 36525;
  return norm(moonLon(T) - sunLon(T));
}
function phaseAt(ms) {
  const [d0, d1] = refDayRange(ms);
  const e0 = elongAt(d0), e1 = elongAt(d1);
  for (const [ang, name] of PRINCIPAL) {           // elongation gains ~12.2deg/day,
    if (sdiff(e0, ang) <= 0 && sdiff(e1, ang) > 0) return name;  // so one crossing max
  }
  return INTERMEDIATE[Math.floor(elongAt(ms) / 90)];
}

function toJD(date) { return date.getTime() / 86400000 + 2440587.5; }

// --- Sun (Meeus ch.25, ~0.01deg) ---
function sunLon(T) {
  const L0 = norm(280.46646 + 36000.76983 * T + 0.0003032 * T * T);
  const M = norm(357.52911 + 35999.05029 * T - 0.0001537 * T * T);
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M * D2R)
          + (0.019993 - 0.000101 * T) * Math.sin(2 * M * D2R) + 0.000289 * Math.sin(3 * M * D2R);
  return norm(L0 + C);
}
// --- Moon (abridged Meeus ch.47, ~0.08deg) ---
function moonLon(T) {
  const Lp = norm(218.3164477 + 481267.88123421 * T - 0.0015786 * T * T);
  const D = norm(297.8501921 + 445267.1114034 * T - 0.0018819 * T * T) * D2R;
  const M = norm(357.5291092 + 35999.0502909 * T) * D2R;
  const Mp = norm(134.9633964 + 477198.8675055 * T + 0.0087414 * T * T) * D2R;
  const F = norm(93.2720950 + 483202.0175233 * T - 0.0036539 * T * T) * D2R;
  const s = 6.288774 * Math.sin(Mp) + 1.274027 * Math.sin(2 * D - Mp) + 0.658314 * Math.sin(2 * D)
    + 0.213618 * Math.sin(2 * Mp) - 0.185116 * Math.sin(M) - 0.114332 * Math.sin(2 * F)
    + 0.058793 * Math.sin(2 * D - 2 * Mp) + 0.057066 * Math.sin(2 * D - M - Mp) + 0.053322 * Math.sin(2 * D + Mp)
    + 0.045758 * Math.sin(2 * D - M) - 0.040923 * Math.sin(M - Mp) - 0.034720 * Math.sin(D)
    - 0.030383 * Math.sin(M + Mp) + 0.015327 * Math.sin(2 * D - 2 * F) - 0.012528 * Math.sin(2 * F + Mp)
    + 0.010980 * Math.sin(2 * F - Mp) + 0.010675 * Math.sin(4 * D - Mp) + 0.010034 * Math.sin(3 * Mp);
  return norm(Lp + s);
}

// --- Planets: Schlyter compact orbital elements (d = days since 2000-01-00 0:00 UT = JD-2451543.5) ---
const ELEM = { // N, i, w, a, e, M as [const, perDay]
  Mercury:{N:[48.3313,3.24587e-5],i:[7.0047,5.00e-8],w:[29.1241,1.01444e-5],a:[0.387098,0],e:[0.205635,5.59e-10],M:[168.6562,4.0923344368]},
  Venus:  {N:[76.6799,2.46590e-5],i:[3.3946,2.75e-8],w:[54.8910,1.38374e-5],a:[0.723330,0],e:[0.006773,-1.302e-9],M:[48.0052,1.6021302244]},
  Mars:   {N:[49.5574,2.11081e-5],i:[1.8497,-1.78e-8],w:[286.5016,2.92961e-5],a:[1.523688,0],e:[0.093405,2.516e-9],M:[18.6021,0.5240207766]},
  Jupiter:{N:[100.4542,2.76854e-5],i:[1.3030,-1.557e-7],w:[273.8777,1.64505e-5],a:[5.20256,0],e:[0.048498,4.469e-9],M:[19.8950,0.0830853001]},
  Saturn: {N:[113.6634,2.38980e-5],i:[2.4886,-1.081e-7],w:[339.3939,2.97661e-5],a:[9.55475,0],e:[0.055546,-9.499e-9],M:[316.9670,0.0334442282]},
  Uranus: {N:[74.0005,1.3978e-5],i:[0.7733,1.9e-8],w:[96.6612,3.0565e-5],a:[19.18171,-1.55e-8],e:[0.047318,7.45e-9],M:[142.5905,0.011725806]},
  Neptune:{N:[131.7806,3.0173e-5],i:[1.7700,-2.55e-7],w:[272.8461,-6.027e-6],a:[30.05826,3.313e-8],e:[0.008606,2.15e-9],M:[260.2471,0.005995147]},
};
function sunGeo(d) { // Schlyter sun: returns {lon, r}
  const w = 282.9404 + 4.70935e-5 * d, e = 0.016709 - 1.151e-9 * d, M = norm(356.0470 + 0.9856002585 * d);
  const E = M + e * R2D * Math.sin(M * D2R) * (1 + e * Math.cos(M * D2R));
  const xv = Math.cos(E * D2R) - e, yv = Math.sqrt(1 - e * e) * Math.sin(E * D2R);
  const v = norm(Math.atan2(yv, xv) * R2D), r = Math.sqrt(xv * xv + yv * yv);
  return { lon: norm(v + w), r };
}
function planetGeoLon(name, d) {
  const el = ELEM[name];
  const N = el.N[0] + el.N[1] * d, i = el.i[0] + el.i[1] * d, w = el.w[0] + el.w[1] * d;
  const a = el.a[0] + el.a[1] * d, e = el.e[0] + el.e[1] * d, M = norm(el.M[0] + el.M[1] * d);
  let E = M + e * R2D * Math.sin(M * D2R) * (1 + e * Math.cos(M * D2R));
  for (let k = 0; k < 2; k++) E = E - (E - e * R2D * Math.sin(E * D2R) - M) / (1 - e * Math.cos(E * D2R));
  const xv = a * (Math.cos(E * D2R) - e), yv = a * Math.sqrt(1 - e * e) * Math.sin(E * D2R);
  const v = norm(Math.atan2(yv, xv) * R2D), r = Math.sqrt(xv * xv + yv * yv);
  const vw = (v + w) * D2R, Nr = N * D2R, ir = i * D2R;
  let xh = r * (Math.cos(Nr) * Math.cos(vw) - Math.sin(Nr) * Math.sin(vw) * Math.cos(ir));
  let yh = r * (Math.sin(Nr) * Math.cos(vw) + Math.cos(Nr) * Math.sin(vw) * Math.cos(ir));
  let zh = r * (Math.sin(vw) * Math.sin(ir));
  // perturbations (Jupiter/Saturn/Uranus) in ecliptic longitude
  const Mj = norm(ELEM.Jupiter.M[0] + ELEM.Jupiter.M[1] * d), Ms = norm(ELEM.Saturn.M[0] + ELEM.Saturn.M[1] * d), Mu = norm(ELEM.Uranus.M[0] + ELEM.Uranus.M[1] * d);
  let lonecl = norm(Math.atan2(yh, xh) * R2D), latecl = Math.atan2(zh, Math.sqrt(xh * xh + yh * yh)) * R2D;
  const S = x => Math.sin(x * D2R);
  if (name === 'Jupiter') lonecl += -0.332 * S(2 * Mj - 5 * Ms - 67.6) - 0.056 * S(2 * Mj - 2 * Ms + 21) + 0.042 * S(3 * Mj - 5 * Ms + 21) - 0.036 * S(Mj - 2 * Ms) + 0.022 * Math.cos((Mj - Ms) * D2R) + 0.023 * S(2 * Mj - 3 * Ms + 52) - 0.016 * S(Mj - 5 * Ms - 69);
  if (name === 'Saturn') lonecl += 0.812 * S(2 * Mj - 5 * Ms - 67.6) - 0.229 * Math.cos((2 * Mj - 4 * Ms - 2) * D2R) + 0.119 * S(Mj - 2 * Ms - 3) + 0.046 * S(2 * Mj - 6 * Ms - 69) + 0.014 * S(Mj - 3 * Ms + 32);
  if (name === 'Uranus') lonecl += 0.040 * S(Ms - 2 * Mu + 6) + 0.035 * S(Ms - 3 * Mu + 33) - 0.015 * S(Mj - Mu + 20);
  const rh = r, cl = Math.cos(lonecl * D2R), sl = Math.sin(lonecl * D2R), cb = Math.cos(latecl * D2R), sb = Math.sin(latecl * D2R);
  xh = rh * cb * cl; yh = rh * cb * sl; zh = rh * sb;
  const s = sunGeo(d); const xs = s.r * Math.cos(s.lon * D2R), ys = s.r * Math.sin(s.lon * D2R);
  return norm(Math.atan2(yh + ys, xh + xs) * R2D); // geocentric ecliptic longitude
}

function bodyLon(name, date) {
  const jd = toJD(date), T = (jd - 2451545.0) / 36525, d = jd - 2451543.5;
  if (name === 'Sun') return sunLon(T);
  if (name === 'Moon') return moonLon(T);
  return planetGeoLon(name, d);
}
function speed(name, date) { // deg/day, numeric
  const dt = 0.5, a = bodyLon(name, new Date(date.getTime() - dt * 86400000)), b = bodyLon(name, new Date(date.getTime() + dt * 86400000));
  let dl = ((b - a + 540) % 360) - 180; return dl / (2 * dt);
}

// --- transit picker: most notable TRUE deterministic event ---
const INNER = ['Mercury','Venus','Mars'], OUTER = ['Jupiter','Saturn','Uranus','Neptune'];
const PLANETS = [...INNER, ...OUTER];
function pickTransit(date) {
  const day = 86400000;
  const at = (n, off) => bodyLon(n, new Date(date.getTime() + off * day));
  // 1) station within +/-2 days (speed sign flips)
  for (const n of PLANETS) {
    const sb = speed(n, new Date(date.getTime() - 2 * day)), sa = speed(n, new Date(date.getTime() + 2 * day));
    if (sb * sa < 0) return { text: n.toLowerCase() + (sa > 0 ? ' stations direct' : ' stations retrograde'), kind: 'station' };
  }
  // 2) ingress within +/-2 days (sign changes)
  for (const n of PLANETS) {
    const s0 = signOf(at(n, -2)), s1 = signOf(at(n, 2));
    if (s0 !== s1) { const now = speed(n, date) >= 0 ? s1 : s0; return { text: n.toLowerCase() + ' enters ' + (signOf(bodyLon(n, date))), kind: 'ingress' }; }
  }
  // 3) active retrograde (inner first, then outer)
  for (const n of [...INNER, ...OUTER]) {
    if (speed(n, date) < 0) return { text: n.toLowerCase() + ' retrograde', kind: 'retro' };
  }
  // 4) fallback: fastest inner planet's sign
  return { text: 'mercury in ' + signOf(bodyLon('Mercury', date)), kind: 'position' };
}

export function computeAlmanac(date = new Date()) {
  const jd = toJD(date), T = (jd - 2451545.0) / 36525;
  const sl = sunLon(T), ml = moonLon(T);
  const elong = norm(ml - sl), ph = phaseAt(date.getTime());
  return {
    sun: { sign: signOf(sl), lon: sl },
    moon: { sign: signOf(ml), lon: ml, phase: ph, illum: Math.round(((1 - Math.cos(elong * D2R)) / 2) * 100) },
    phaseKey: ph, art: PHASE_ART[ph],
    transit: pickTransit(date),
  };
}

export { bodyLon, speed, signOf, PHASES, PHASE_ART, phaseAt, elongAt };

// --- browser bootstrap: populate the sky strip on load (no-op under Node) ---
if (typeof document !== 'undefined') {
  const run = () => {
    const root = document.querySelector('.rit-sky[data-almanac]');
    if (!root) return;
    let a;
    try { a = computeAlmanac(new Date()); } catch (e) { return; }
    const set = (sel, txt) => { const el = root.querySelector(sel); if (el) el.textContent = txt; };
    set('[data-sun]', a.sun.sign);
    set('[data-moon]', a.moon.sign);
    set('[data-phase]', a.moon.phase);
    const override = (root.getAttribute('data-transit-override') || '').trim();
    set('[data-transit]', override || a.transit.text);
    const img = root.querySelector('.rit-sky__moon img');
    const urls = window.__RIT_MOON || {};
    if (img && urls[a.art.img]) {
      img.src = urls[a.art.img];
      img.style.transform = a.art.flip ? 'scaleX(-1)' : 'none';
      img.alt = 'the moon tonight, ' + a.moon.phase;
      root.querySelector('.rit-sky__moon').classList.toggle('is-new', !!a.art.dim);
    }
    root.classList.add('rit-sky--ready');
  };
  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
}
